/**
 * API Client — typed fetch wrapper for the backend REST API.
 *
 * All requests go through this client for consistent error handling,
 * base URL resolution, and response typing.
 */

import { captureFreshnessFromMeta } from './dataFreshness';

const BASE_URL = '/api';

// Absolute Cloud Run origin, injected at build time (same value the SSE stream
// uses). In production the default `/api` path is a Firebase Hosting rewrite,
// which hard-caps every request at 60s and returns 504 regardless of the Cloud
// Run timeout. Heavy endpoints (marketing pivot/time-series) opt into calling
// Cloud Run directly via this origin to escape that 60s ceiling. Empty in dev
// (Vite proxy handles `/api` with no timeout), so `direct` falls back to `/api`.
const DIRECT_API_BASE = ((import.meta as any).env?.VITE_API_STREAM_URL as string | undefined) ?? '';

/** Resolve an endpoint to a full URL, optionally bypassing the Hosting proxy. */
function resolveUrl(endpoint: string, direct?: boolean): string {
  if (direct && DIRECT_API_BASE) return `${DIRECT_API_BASE}${BASE_URL}${endpoint}`;
  return `${BASE_URL}${endpoint}`;
}

// ─── Auth Token Provider ────────────────────────────────────────────────────

type TokenProvider = () => Promise<string | null>;
let _getToken: TokenProvider = async () => null;

/** Called once by AuthProvider to wire up token retrieval. */
export function setApiTokenProvider(provider: TokenProvider): void {
  _getToken = provider;
}

/** The current user's API token, for requests made outside `api` (e.g. chat transports). */
export function getApiToken(): Promise<string | null> {
  return _getToken();
}

/** Full URL for an API path, honouring the direct (streaming) base when set. */
export function apiUrl(endpoint: string, direct = false): string {
  return resolveUrl(endpoint, direct);
}

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: Record<string, unknown>;
}

export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export class ApiRequestError extends Error {
  public status: number;
  public code: string;
  public details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// ─── Core Fetch ─────────────────────────────────────────────────────────────

const DEFAULT_TIMEOUT_MS = 30_000;
const TOKEN_TIMEOUT_MS = 5_000;

async function getTokenWithTimeout(): Promise<string | null> {
  try {
    const result = await Promise.race([
      _getToken(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS)),
    ]);
    return result;
  } catch {
    return null;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  direct?: boolean,
): Promise<T> {
  const url = resolveUrl(endpoint, direct);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const token = await getTokenWithTimeout();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      // Never let the browser HTTP-cache an API body. Without this, cacheable
      // GETs (e.g. the workspace file listing) can survive a reload and show
      // already-deleted files as if a "sync" never landed.
      cache: 'no-store',
      signal: controller.signal,
    });
  } catch (err: any) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new ApiRequestError(0, 'TIMEOUT', `Request to ${endpoint} timed out after ${timeoutMs}ms`);
    }
    throw new ApiRequestError(0, 'NETWORK_ERROR', err.message ?? 'Network request failed');
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  let json: any;
  try {
    json = await response.json();
  } catch {
    if (response.ok) {
      return undefined as T;
    }
    throw new ApiRequestError(
      response.status,
      'PARSE_ERROR',
      `Failed to parse response from ${endpoint} (status ${response.status})`,
    );
  }

  if (!response.ok) {
    const error = json as ApiError;
    throw new ApiRequestError(
      response.status,
      error.error?.code || 'UNKNOWN_ERROR',
      error.error?.message || 'An unexpected error occurred',
      error.error?.details,
    );
  }

  const body = json as ApiResponse<T>;
  captureFreshnessFromMeta(body.meta);
  return body.data;
}

/** Like request(), but returns both data and meta */
async function requestFull<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<{ data: T; meta?: Record<string, unknown> }> {
  const url = `${BASE_URL}${endpoint}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const token = await getTokenWithTimeout();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      // Never let the browser HTTP-cache an API body. Without this, cacheable
      // GETs (e.g. the workspace file listing) can survive a reload and show
      // already-deleted files as if a "sync" never landed.
      cache: 'no-store',
      signal: controller.signal,
    });
  } catch (err: any) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new ApiRequestError(0, 'TIMEOUT', `Request to ${endpoint} timed out after ${timeoutMs}ms`);
    }
    throw new ApiRequestError(0, 'NETWORK_ERROR', err.message ?? 'Network request failed');
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 204) {
    return { data: undefined as T };
  }

  let json: any;
  try {
    json = await response.json();
  } catch {
    if (response.ok) {
      return { data: undefined as T };
    }
    throw new ApiRequestError(
      response.status,
      'PARSE_ERROR',
      `Failed to parse response from ${endpoint} (status ${response.status})`,
    );
  }

  if (!response.ok) {
    const error = json as ApiError;
    throw new ApiRequestError(
      response.status,
      error.error?.code || 'UNKNOWN_ERROR',
      error.error?.message || 'An unexpected error occurred',
      error.error?.details,
    );
  }

  const body = json as ApiResponse<T>;
  captureFreshnessFromMeta(body.meta);
  return { data: body.data, meta: body.meta };
}

// ─── HTTP Methods ───────────────────────────────────────────────────────────

export const api = {
  get<T>(endpoint: string, params?: Record<string, string>, timeoutMs?: number): Promise<T> {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<T>(`${endpoint}${qs}`, {}, timeoutMs);
  },

  /** GET that also returns response meta (e.g. pagination totals) */
  getWithMeta<T>(
    endpoint: string,
    params?: Record<string, string>,
  ): Promise<{ data: T; meta?: Record<string, unknown> }> {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return requestFull<T>(`${endpoint}${qs}`);
  },

  post<T>(endpoint: string, body?: unknown, timeoutMs?: number, direct?: boolean): Promise<T> {
    return request<T>(
      endpoint,
      {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      },
      timeoutMs,
      direct,
    );
  },

  /** POST that also returns response meta alongside data */
  postWithMeta<T>(endpoint: string, body?: unknown): Promise<{ data: T; meta?: Record<string, unknown> }> {
    return requestFull<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  delete<T = void>(endpoint: string): Promise<T> {
    return request<T>(endpoint, { method: 'DELETE' });
  },
};
